/**
 * @file jobApplicationDialog.component.tsx
 * @path apps/website/src/features/recruitment/components/jobApplicationDialog.component.tsx
 * @description Pure presentational component for the application form.
 */

import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@sirbastians/ui";
import { Mail, Upload, X, FileText, Loader2, CheckCircle } from "lucide-react";
import { formatFileSize } from "@/utils";
import { useRecruitmentFacade } from "../hooks/useRecruitmentFacade.hook";

interface JobApplicationDialogProps {
  facade: ReturnType<typeof useRecruitmentFacade>["application"];
}

export function JobApplicationDialog({ facade }: JobApplicationDialogProps) {
  const { form, dialogJob, fileState, submissionState, actions } = facade;
  const isOpen = !!dialogJob;

  if (!dialogJob) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && actions.close()}>
      <DialogContent className="max-w-md">
        {submissionState.isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="mb-4 h-16 w-16 text-green-500" />
            <DialogTitle className="mb-2 text-2xl">
              Application Submitted!
            </DialogTitle>
            <DialogDescription className="text-base">
              Thank you for applying for the <strong>{dialogJob.title}</strong>{" "}
              position. We'll review your application and get back to you within
              5-7 business days.
            </DialogDescription>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Apply for {dialogJob.title}</DialogTitle>
              <DialogDescription>{dialogJob.location}</DialogDescription>
            </DialogHeader>

            <form onSubmit={actions.submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">
                  Full Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="fullName"
                  {...form.register("fullName")}
                  placeholder="John Doe"
                  disabled={submissionState.isSubmitting}
                />
                {form.formState.errors.fullName && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.fullName.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email Address <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...form.register("email")}
                  placeholder="john@example.com"
                  disabled={submissionState.isSubmitting}
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">
                  Phone Number <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  {...form.register("phone")}
                  placeholder="+27 12 345 6789"
                  disabled={submissionState.isSubmitting}
                />
                {form.formState.errors.phone && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.phone.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="cv-upload">
                  CV/Resume <span className="text-destructive">*</span>
                </Label>
                <div className="space-y-2">
                  {!fileState.file ? (
                    <label
                      htmlFor="cv-upload"
                      className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted/50 px-4 py-8 text-sm transition-colors hover:border-accent hover:bg-muted"
                    >
                      <Upload className="h-5 w-5 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        Click to upload PDF, DOC, or DOCX
                      </span>
                      <input
                        id="cv-upload"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={actions.handleFileChange}
                        disabled={submissionState.isSubmitting}
                        className="hidden"
                      />
                    </label>
                  ) : (
                    <div className="flex items-center justify-between rounded-md border border-border bg-muted/50 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-accent" />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {fileState.file.name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatFileSize(fileState.file.size)}
                          </span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={actions.removeFile}
                        disabled={submissionState.isSubmitting}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                {fileState.error && (
                  <p className="text-sm text-destructive">{fileState.error}</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={actions.close}
                  disabled={submissionState.isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submissionState.isSubmitting || !fileState.file}
                  className="flex-1"
                >
                  {submissionState.isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Submit Application
                    </>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
